import type { ThoughtIdentityConfig, Speaker } from '../content/types';
import type { MediaSnapshot } from './MediaDirector';

/** Optional entrance tones, routed through the same mute gain as the dialogue. */
export class ThoughtAudioFeedback {
  enabled = true;
  announcements = 0;
  lastSpeaker: Speaker | null = null;
  private announcedCue: string | null = null;
  private context?: AudioContext;
  private destination?: AudioNode;
  private config?: ThoughtIdentityConfig;
  private source?: AudioBufferSourceNode;
  private readonly buffers = new Map<Speaker, AudioBuffer>();

  configure(config: ThoughtIdentityConfig): void { this.config = config; this.buffers.clear(); }
  connect(context: AudioContext, destination: AudioNode): void { this.context = context; this.destination = destination; }
  setEnabled(enabled: boolean): void { this.enabled = enabled; if (!enabled) this.stop(); }
  stop(): void { this.source?.stop(); this.source = undefined; }

  update(state: MediaSnapshot): void {
    const cue = state.cue?.kind === 'thought' ? state.cue : null;
    if (!state.playing || state.seeking || state.muted || !this.enabled || !cue) this.stop();
    if (!cue) { this.announcedCue = null; return; }
    if (!state.playing || state.seeking || cue.id === this.announcedCue) return;
    this.announcedCue = cue.id;
    // Do not announce stale thoughts after an arbitrary seek into their middle.
    if (state.muted || !this.enabled || state.time - cue.start > 0.75) return;
    if (!this.context || this.context.state !== 'running' || !this.destination || !this.config || !cue.speaker) return;
    this.stop();
    let buffer = this.buffers.get(cue.speaker);
    if (!buffer) {
      const { duration, amplitude } = this.config.sounds;
      buffer = this.context.createBuffer(1, Math.round(duration * this.context.sampleRate), this.context.sampleRate);
      const data = buffer.getChannelData(0), frequencies = this.config.sounds[cue.speaker];
      for (let i=0;i<data.length;i++) {
        const t=i/this.context.sampleRate, note=Math.min(frequencies.length-1,Math.floor(t/duration*frequencies.length));
        const local=t-note*duration/frequencies.length, length=duration/frequencies.length;
        const envelope=Math.sin(Math.PI*local/length)**2;
        data[i]=amplitude*envelope*Math.sin(2*Math.PI*frequencies[note]*local);
      }
      this.buffers.set(cue.speaker,buffer);
    }
    const source=this.context.createBufferSource();
    source.buffer=buffer;source.connect(this.destination);source.start();
    this.source=source;this.announcements++;this.lastSpeaker=cue.speaker;
    source.onended=()=>{if(this.source===source)this.source=undefined;source.disconnect();};
  }
}
